import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close,
  MailOutline,
  ReplayOutlined,
  SendOutlined,
  AttachFileOutlined,
} from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import * as dayjs from "dayjs";
import { useNotification } from "../../notificationProvider";
import usePermissao from "../../../hooks/usePermissao";
import { errorMessage, findManyEmailSends, resendEmail } from "./api";
import SendStatusChip, { SEND_STATUS } from "./sendStatus";
import { EmailSend } from "./types";

interface EmailSendsDialogProps {
  open: boolean;
  onClose: () => void;
  relatorio: { id: string; etapa: number; dataGeracao: Date | string } | null;
  totalEtapas: number;
  emailEnabled: boolean;
  onChanged: () => void;
  onConfigure: () => void;
}

const NO_RECIPIENT = "(nenhum)";

const formatDate = (date: string | null) => (date ? dayjs(date).format("DD/MM/YYYY [às] HH:mm") : "");

const formatSize = (bytes: number | null) =>
  bytes ? `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB` : "";

export default function EmailSendsDialog({
  open,
  onClose,
  relatorio,
  totalEtapas,
  emailEnabled,
  onChanged,
  onConfigure,
}: EmailSendsDialogProps) {
  const theme = useTheme();
  const notify = useNotification();
  const { canEdit } = usePermissao("easyReport");
  const [sends, setSends] = useState<EmailSend[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);

  const load = useCallback(async () => {
    if (!relatorio) return;
    setLoading(true);
    try {
      const data = await findManyEmailSends({ agendamentoRelatorioId: relatorio.id, porPagina: 100 });
      setSends(
        [...data.envios].sort((a, b) => dayjs(b.createAt).valueOf() - dayjs(a.createAt).valueOf()),
      );
    } catch (error) {
      notify(errorMessage(error, "Erro ao carregar envios."), "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [relatorio?.id]);

  useEffect(() => {
    if (open) {
      setBlocked(false);
      setSends([]);
      load();
    }
  }, [open, load]);

  const resend = async (destinatario?: string) => {
    if (!relatorio) return;
    setSending(destinatario ?? "__all__");
    try {
      await resendEmail({
        agendamentoRelatorioId: relatorio.id,
        ...(destinatario ? { destinatario } : {}),
      });
      notify(destinatario ? `Reenviado para ${destinatario}` : "E-mail reenviado", "success", 3000);
      await load();
      onChanged();
    } catch (error: any) {
      if (error?.response?.status === 409) setBlocked(true);
      notify(errorMessage(error, "Não foi possível reenviar."), "error", 4000);
    } finally {
      setSending(null);
    }
  };

  const latestByRecipient = sends.filter(
    (s, i) => sends.findIndex((o) => o.destinatario === s.destinatario) === i,
  );
  const noRecipientError = latestByRecipient.some(
    (s) => s.destinatario === NO_RECIPIENT && s.status === SEND_STATUS.FALHA_DEFINITIVA,
  );
  const rows = latestByRecipient.filter((s) => s.destinatario !== NO_RECIPIENT);
  const busy = sends.some(
    (s) => s.status === SEND_STATUS.PENDENTE || s.status === SEND_STATUS.ENVIANDO,
  );
  const canResend = !blocked && !busy && !sending;

  const detail = (s: EmailSend) => {
    const parts: string[] = [];
    const when = formatDate(s.enviadoEm ?? s.createAt);
    if (when) parts.push(when);
    if (s.tentativas > 1) parts.push(`${s.tentativas} tentativas`);
    if (s.status === SEND_STATUS.FALHA_TEMPORARIA && s.proximaTentativa) {
      parts.push(`nova tentativa ${formatDate(s.proximaTentativa)}`);
    }
    return parts.join(" · ");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: "primary.main",
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            }}
          >
            <MailOutline fontSize="small" />
          </Box>
          <Box>
            <Typography fontSize={18} fontWeight={600}>
              Envios por e-mail
            </Typography>
            {relatorio && (
              <Typography fontSize={13} color="text.secondary">
                Relatório {relatorio.etapa}/{totalEtapas} · gerado em{" "}
                {dayjs(relatorio.dataGeracao).format("DD/MM/YYYY")}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton aria-label="Fechar" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {noRecipientError && (
          <Alert
            severity="error"
            action={
              canEdit && (
                <Button color="inherit" size="small" onClick={onConfigure}>
                  Configurar
                </Button>
              )
            }
          >
            Não havia nenhum destinatário ativo quando este relatório foi gerado.
          </Alert>
        )}

        {loading && !sends.length ? (
          [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={56} />)
        ) : rows.length === 0 && !noRecipientError ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 4,
              color: "text.secondary",
            }}
          >
            <MailOutline sx={{ fontSize: 40, color: "text.disabled" }} />
            <Typography>Este relatório ainda não foi enviado por e-mail</Typography>
          </Box>
        ) : (
          rows.map((s) => (
            <Box
              key={s.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${theme.palette.stroke}`,
              }}
            >
              <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: alpha(theme.palette.text.primary, 0.08), color: "text.primary" }}>
                {(s.nomeDestinatario || s.destinatario).charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontSize={14} fontWeight={500} noWrap>
                  {s.nomeDestinatario ? `${s.nomeDestinatario} · ${s.destinatario}` : s.destinatario}
                </Typography>
                <Typography fontSize={12} color="text.secondary" noWrap>
                  {detail(s)}
                </Typography>
                {s.status === SEND_STATUS.FALHA_DEFINITIVA && s.erro && (
                  <Typography fontSize={12} color="error.main" sx={{ wordBreak: "break-word" }}>
                    {s.erro}
                  </Typography>
                )}
                {!s.anexado && s.status === SEND_STATUS.ACEITO && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="info"
                    icon={<AttachFileOutlined />}
                    label={`Enviado sem anexo${s.tamanhoAnexo ? ` (PDF de ${formatSize(s.tamanhoAnexo)})` : ""}`}
                    sx={{ mt: 0.5, height: 20, fontSize: 11 }}
                  />
                )}
              </Box>
              <SendStatusChip status={s.status} />
              {canEdit && (
                <Tooltip title="Reenviar">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canResend}
                      onClick={() => resend(s.destinatario)}
                    >
                      {sending === s.destinatario ? (
                        <CircularProgress size={18} />
                      ) : (
                        <ReplayOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          ))
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose}>
          Fechar
        </Button>
        {canEdit && (
        <Tooltip title={!emailEnabled ? "O envio por e-mail está desativado neste agendamento" : ""}>
          <span>
            <Button
              variant="contained"
              startIcon={
                sending === "__all__" ? <CircularProgress size={16} color="inherit" /> : <SendOutlined />
              }
              disabled={!canResend || !emailEnabled}
              onClick={() => resend()}
            >
              {rows.length ? "Reenviar para todos" : "Enviar agora"}
            </Button>
          </span>
        </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
}
